import {getSignificanceBasedPrecision, human} from '@github-ui/formatters'
import {ListView, useListViewVariant} from '@github-ui/list-view'
import type {RepositoryItem} from '@github-ui/repos-list-shared'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {Box, Text} from '@primer/react'
import {type ComponentProps, useRef} from 'react'

import {CompactListItem, DefaultListItem, NarrowCompactListItem} from './ReposListItem'
import {SortingDropdown} from './SortingDropdown'

export type ListViewVariant = ComponentProps<typeof ListView>['variant']

export function ReposList({
  compactMode,
  repositoryCount,
  repos,
  sortingItemSelected,
  onSortingItemSelect,
}: {
  compactMode: boolean
  repositoryCount: number
  repos: RepositoryItem[]
  sortingItemSelected?: string
  onSortingItemSelect: (key: string) => void
}) {
  const savedCompactModeRef = useRef(compactMode)
  function updateCompactModeSetting(value: boolean) {
    if (savedCompactModeRef.current === value) {
      return
    }

    savedCompactModeRef.current = value

    const formData = new FormData()
    formData.set('orgs_repos_compact_mode', value.toString())
    verifiedFetch('/repos/preferences', {method: 'PUT', body: formData})
  }

  const metadata = (
    <ListView.Metadata
      title={<Text sx={{fontWeight: 'bold'}}>{getDisplayReposCount(repositoryCount)}</Text>}
      densityToggle={<ListView.DensityToggle />}
    >
      <SortingDropdown sortingItemSelected={sortingItemSelected || ''} onSortingItemSelect={onSortingItemSelect} />
    </ListView.Metadata>
  )

  return (
    <Box sx={{border: '1px solid', borderColor: 'border.muted', borderRadius: 2}}>
      <ListView
        onVariantChange={value => updateCompactModeSetting(value === 'compact')}
        variant={compactMode ? 'compact' : 'default'}
        metadata={metadata}
        title="Repositories list"
        titleHeaderTag="h2"
      >
        {repos.map(repo => (
          <ReposListItem key={repo.name} repo={repo} />
        ))}
      </ListView>
    </Box>
  )
}

function ReposListItem({repo}: {repo: RepositoryItem}) {
  const {variant} = useListViewVariant()
  if (variant === 'default') {
    return <DefaultListItem repo={repo} />
  }

  return (
    <>
      <CompactListItem repo={repo} sx={{display: ['none', 'flex']}} />
      <NarrowCompactListItem repo={repo} sx={{display: ['block', 'none'], px: 2, pb: 2}} />
    </>
  )
}

export function getDisplayReposCount(count: number): string {
  const displayCount = human(count, {
    precision: getSignificanceBasedPrecision(count),
  })

  return ` ${displayCount} ${count === 1 ? 'repository' : 'repositories'}`
}

try{ ReposList.displayName ||= 'ReposList' } catch {}
try{ ReposListItem.displayName ||= 'ReposListItem' } catch {}